import { useQuery } from '@apollo/client';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import AddBox from '@mui/icons-material/AddBox';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import Check from '@mui/icons-material/Check';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Clear from '@mui/icons-material/Clear';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import Edit from '@mui/icons-material/Edit';
import FilterList from '@mui/icons-material/FilterList';
import FirstPage from '@mui/icons-material/FirstPage';
import LastPage from '@mui/icons-material/LastPage';
import ViewIcon from '@mui/icons-material/OpenInBrowser';
import Remove from '@mui/icons-material/Remove';
import SaveAlt from '@mui/icons-material/SaveAlt';
import Search from '@mui/icons-material/Search';
import ViewColumn from '@mui/icons-material/ViewColumn';
import dayjs from 'dayjs';
import MaterialTable from '@material-table/core';
import React, { forwardRef, useEffect, useState } from 'react';
import { GET_PRIVATE_POSTS } from '../../../helpers/graphql/posts';
import { ThemeProvider } from '@mui/material/styles';
import { teal } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
  View: forwardRef((props, ref) => <ViewIcon {...props} ref={ref} />),
};

tableIcons.Add.displayName = 'Add';
tableIcons.Check.displayName = 'Check';
tableIcons.Clear.displayName = 'Clear';
tableIcons.Delete.displayName = 'Delete';
tableIcons.DetailPanel.displayName = 'DetailPanel';
tableIcons.Edit.displayName = 'Edit';
tableIcons.Export.displayName = 'Export';
tableIcons.Filter.displayName = 'Filter';
tableIcons.FirstPage.displayName = 'FirstPage';
tableIcons.LastPage.displayName = 'LastPage';
tableIcons.NextPage.displayName = 'NextPage';
tableIcons.PreviousPage.displayName = 'PreviousPage';
tableIcons.ResetSearch.displayName = 'ResetSearch';
tableIcons.Search.displayName = 'Search';
tableIcons.SortArrow.displayName = 'SortArrow';
tableIcons.ThirdStateCheck.displayName = 'ThirdStateCheck';
tableIcons.ViewColumn.displayName = 'ViewColumn';
tableIcons.View.displayName = 'View';

export default function SelectPostsTable({ setSelectedPermlinks }) {
  const theme = useTheme();
  theme.palette.secondary.main = teal[500];
  theme.palette.secondary.light = teal[500];
  theme.palette.secondary.dark = teal[500];

  const [posts, setPosts] = useState();
  const [fullLoaded, setFullLoaded] = useState(false);

  const handleSelect = selectedRows => {
    const selectedPermlinks = [];
    selectedRows.forEach(({ permlink }) => {
      selectedPermlinks.push(permlink);
    });
    setSelectedPermlinks(selectedPermlinks);
  };

  const handleNewPosts = postData => {
    const newPosts = [];
    postData.forEach(post => {
      const {
        title,
        permlink,
        excerpt,
        thumbnail,
        blacklist,
        hiveAuthor,
        hivePerm,
        steemAuthor,
        steemPerm,
        evaluation,
        comments,
        totalSmiles,
        views,
      } = post;
      const updatedAt = dayjs(post.updatedAt);
      newPosts.push({
        title,
        permlink,
        excerpt,
        thumbnail,
        updatedAt,
        blacklist,
        hiveAuthor,
        hivePerm,
        steemAuthor,
        steemPerm,
        evaluation,
        comments: comments || 0,
        totalSmiles: totalSmiles || 0,
        views: views || 0,
      });
    });
    setPosts(newPosts);
  };

  const handleCompleted = postData => {
    if (
      !postData ||
      !postData.privatePosts ||
      !postData.privatePosts.length === 0
    )
      return;
    // Initially fetch 10 posts, then fetch the rest
    handleNewPosts(postData.privatePosts);
    if (!fullLoaded && postData.privatePosts.length === 10) {
      setFullLoaded(true);
      // eslint-disable-next-line no-use-before-define
      if (fetchMore)
        fetchMore({
          variables: {
            offset: postData.privatePosts.length,
            limit: 500,
          },
          // eslint-disable-next-line consistent-return
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
              return prev;
            }
            handleNewPosts([
              ...prev.privatePosts,
              ...fetchMoreResult.privatePosts,
            ]);
          },
        });
    }
  };

  const { fetchMore, refetch } = useQuery(GET_PRIVATE_POSTS, {
    variables: { limit: 10 },
    // eslint-disable-next-line no-use-before-define
    onCompleted: handleCompleted,
    ssr: false,
  });

  useEffect(() => {
    if (refetch) {
      refetch();
    }
  }, []);

  if (!posts)
    return (
      <Grid container spacing={0} alignItems="center" justifyContent="center">
        <Grid item lg={12} md={12} sm={12} xs={12} key="loader">
          <div className="p-5 text-center">
            <CircularProgress />
          </div>
        </Grid>
      </Grid>
    );

  return (
    <>
      <div style={{ maxWidth: '100%' }}>
        <ThemeProvider theme={theme}>
          <MaterialTable
            icons={tableIcons}
            columns={[
              {
                title: 'Title',
                field: 'title',
              },
              {
                title: 'Last edit',
                field: 'updatedAt',
                type: 'numeric',
                render: rowData =>
                  `${rowData.updatedAt.format('MMMM DD, YYYY')}`,
              },
            ]}
            data={posts}
            title="Posts"
            options={{ pageSize: 10, selection: true }}
            onSelectionChange={handleSelect}
          />
        </ThemeProvider>
      </div>
    </>
  );
}
